import { type Page, PageListInput, type PageListQuery } from "@cleanbudget/contract/page";
import { IconPlus } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, stripSearchParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { BackLink } from "@/components/back-link";
import { ButtonLink } from "@/components/button-link";
import { type Column, DataTable } from "@/components/data-table/data-table";
import { DataTableEmpty } from "@/components/data-table/empty";
import { DataTablePagination } from "@/components/data-table/pagination";
import { DataTableToolbar, FilterSelect, SearchInput } from "@/components/data-table/toolbar";
import { PageHeader } from "@/components/page-header";
import { PublishedBadge } from "@/components/published-badge";
import { formatDateTime } from "@/lib/format";
import { orpc } from "@/lib/orpc";

const pagesQuery = (query: PageListQuery) => orpc.page.list.queryOptions({ input: query });

/** The list's defaults, left out of the URL so a plain `/content/pages` is the default view. */
const { sort, order, page, pageSize } = PageListInput.parse({});

/** Pagina's: every page, published or not, searchable on title and slug. */
export const Route = createFileRoute("/_admin/content/pages/")({
	validateSearch: PageListInput,
	search: { middlewares: [stripSearchParams({ sort, order, page, pageSize })] },
	loaderDeps: ({ search }) => search,
	loader: ({ context, deps }) => context.queryClient.ensureQueryData(pagesQuery(deps)),
	component: PagesPage,
});

function PagesPage() {
	const { t } = useTranslation("page");
	const { t: tCommon } = useTranslation();
	const { t: tContent } = useTranslation("content");
	const search = Route.useSearch();
	const { data } = useSuspenseQuery(pagesQuery(search));

	const columns: readonly Column<Page>[] = [
		{
			id: "title",
			header: t("column.title"),
			sortable: true,
			cell: (row) => (
				<span className="flex flex-col">
					<Link
						to="/content/pages/$id"
						params={{ id: row.id }}
						className="font-medium underline-offset-2 hover:underline"
					>
						{row.title}
					</Link>
					{/* On a phone the slug column is hidden; show it under the title. */}
					<span className="text-xs text-muted-foreground md:hidden">/{row.slug}</span>
				</span>
			),
		},
		{
			id: "slug",
			header: t("column.slug"),
			secondary: true,
			cell: (row) => <span className="text-muted-foreground">/{row.slug}</span>,
		},
		{
			id: "status",
			header: t("column.status"),
			cell: (row) => <PublishedBadge published={row.published} />,
		},
		{
			id: "updatedAt",
			header: t("column.updated"),
			sortable: true,
			secondary: true,
			cell: (row) => <time dateTime={row.updatedAt}>{formatDateTime(row.updatedAt)}</time>,
		},
	];

	const filtered = Boolean(search.search) || search.published !== undefined;
	const pastEnd = data.items.length === 0 && data.total > 0;

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				back={<BackLink to="/content">{tContent("title")}</BackLink>}
				title={t("title")}
				description={t("description")}
				actions={
					<ButtonLink to="/content/pages/new">
						<IconPlus aria-hidden />
						{t("new.title")}
					</ButtonLink>
				}
			/>
			<div className="flex flex-col gap-3">
				<DataTableToolbar>
					<SearchInput value={search.search} label={t("searchLabel")} />
					<FilterSelect
						param="published"
						label={tCommon("published.label")}
						value={search.published}
						options={[
							{ value: "yes", label: tCommon("published.yes") },
							{ value: "no", label: tCommon("published.no") },
						]}
					/>
				</DataTableToolbar>
				<DataTable
					label={t("title")}
					columns={columns}
					rows={data.items}
					rowKey={(row) => row.id}
					sort={search.sort}
					order={search.order}
					empty={<DataTableEmpty filtered={filtered} pastEnd={pastEnd} nothingYet={t("empty")} />}
				/>
				<DataTablePagination page={data.page} pageSize={data.pageSize} total={data.total} />
			</div>
		</div>
	);
}
